import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from './ui/button';
import { MapConfig } from '@/types';
import { PosterSettings } from '@/types/canvas';
import { ShoppingCart, ChevronRight, X } from 'lucide-react';
import { getAllVariants } from '@/lib/variants';

interface ECommerceCardProps {
  config: MapConfig;
  disabled?: boolean;
  loading?: boolean;
  onCheckout: (price?: number) => void;
  posterSettings?: PosterSettings;
}

export function ECommerceCard({ config, disabled, loading, onCheckout, posterSettings }: ECommerceCardProps) {
  const { t } = useTranslation();
  const [isCollapsed, setIsCollapsed] = useState(true); // Defaultně sbalené

  const [prices, setPrices] = useState({ poster: 39.90, hanger: 14.90, frame: 39.90 });

  useEffect(() => {
    async function loadPrices() {
      try {
        // Use the selected size, default to 'B2' if not set
        const currentSize = posterSettings?.size || 'B2';
        const variants = await getAllVariants({ size: currentSize, onlyAvailable: false });

        const posterVariant = variants.find(v => v.frame_type === 'none');
        const frameVariant = variants.find(v => v.frame_type === 'frame' && v.frame_color === 'pine');
        const hangerVariant = variants.find(v => v.frame_type === 'hanger' && v.frame_color === 'pine');

        // Fallback prices based on size
        const fallbackPosterPrice = currentSize === 'A2' ? 29.90 : 39.90;
        const fallbackFramePrice = currentSize === 'A2' ? 39.90 : 39.90; // Frame usually for B2
        const fallbackHangerPrice = currentSize === 'A2' ? 14.90 : 14.90;

        setPrices({
          poster: posterVariant?.price_cents ? posterVariant.price_cents / 100 : fallbackPosterPrice,
          frame: frameVariant?.price_cents ? frameVariant.price_cents / 100 : fallbackFramePrice,
          hanger: hangerVariant?.price_cents ? hangerVariant.price_cents / 100 : fallbackHangerPrice,
        });
      } catch (error) {
        console.error('Error loading prices:', error);
      }
    }
    loadPrices();
  }, [posterSettings?.size]);

  // Determine current configuration (prioritizing posterSettings)
  const frame = posterSettings?.frame || config?.design?.frame || 'none';
  const frameColor = posterSettings?.frameColor || config?.design?.frameColor || 'pine';
  const size = posterSettings?.size || 'B2';

  // Výpočet ceny na základě výběru hangeru/rámu
  let discountedPrice = prices.poster; // Základní cena printu
  if (frame === 'hanger') {
    discountedPrice += prices.hanger; // Přidat cenu hangeru
  } else if (frame === 'frame') {
    discountedPrice += prices.frame; // Přidat cenu rámu
  }

  const basePrice = discountedPrice / 0.7; // 30% sleva

  const getSizeLabel = (size: string) => {
    switch (size) {
      case 'B2':
        return 'B2 (50 × 70 cm)';
      default:
        return size;
    }
  };

  const getFrameColorLabel = (color?: string) => {
    if (!color) return '—';
    return t(`checkout.frameColors.${color}`, { defaultValue: color });
  };

  // Collapsed stav - ikona košíku s badge cen přes ikonu
  if (isCollapsed) {
    return (
      <button
        onClick={() => setIsCollapsed(false)}
        className="relative bg-posterden-red hover:bg-posterden-dark rounded-2xl border-2 border-posterden-red/50 shadow-2xl p-3 transition-all duration-200 w-auto ml-auto"
        aria-label={t('ecommerce.cart', { defaultValue: 'Košík' })}
      >
        <ShoppingCart className="w-8 h-8 text-white" />
      </button>
    );
  }

  return (
    <div className="bg-boho-cream/90 backdrop-blur-lg rounded-2xl border border-boho-gold/30 shadow-xl flex flex-col relative w-[320px]">
      {/* Křížek pro zavření karty v pravém horním rohu */}
      <button
        onClick={() => setIsCollapsed(true)}
        className="absolute -right-2 -top-2 bg-boho-cream/90 backdrop-blur-lg rounded-full border border-boho-gold/30 shadow-md p-1.5 hover:bg-white transition-all duration-200 z-10"
        aria-label={t('ecommerce.closeCart', { defaultValue: 'Zavřít košík' })}
      >
        <X className="w-4 h-4 text-foreground" />
      </button>

      {/* Seznam produktů - vždy otevřený */}
      <div className="p-4 pb-2">
        <div className="mb-3">
          <h3 className="text-sm font-medium text-foreground">
            {t('ecommerce.cart', { defaultValue: 'Košík' })}
          </h3>
        </div>
        <div className="space-y-2 text-sm">
          {/* Poster - vždy zobrazen */}
          <div className="flex justify-between items-center">
            <span className="text-muted-foreground">
              {t('checkout.poster', { defaultValue: 'Poster' })} ({getSizeLabel(size)})
            </span>
            <span className="font-medium text-foreground">
              {prices.poster.toFixed(2)}€
            </span>
          </div>

          {/* Hanger - pokud je vybrán */}
          {frame === 'hanger' && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">
                {t('checkout.hanger', { defaultValue: 'Magnetic Hanger' })} ({getFrameColorLabel(frameColor)})
              </span>
              <span className="font-medium text-foreground">{prices.hanger.toFixed(2)}€</span>
            </div>
          )}

          {/* Frame - pokud je vybrán */}
          {frame === 'frame' && (
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">
                {t('checkout.frame', { defaultValue: 'Wooden Frame' })} ({getFrameColorLabel(frameColor)})
              </span>
              <span className="font-medium text-foreground">{prices.frame.toFixed(2)}€</span>
            </div>
          )}
        </div>
      </div>

      {/* CTA Button - Přejít k pokladně */}
      <div className="p-4 pt-2">
        <Button
          onClick={() => onCheckout(discountedPrice)}
          disabled={disabled || loading}
          className={`w-full flex items-center justify-between gap-3 py-8 px-6 text-lg font-bold transition-all duration-200 rounded-xl ${disabled || loading
            ? 'bg-boho-dark text-boho-beige cursor-not-allowed'
            : 'bg-posterden-red hover:bg-posterden-dark text-white shadow-lg hover:shadow-xl hover:scale-[1.02]'
            }`}
        >
          {loading ? (
            <div className="flex items-center gap-2">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current"></div>
              <span>{t('ecommerce.processing', { defaultValue: 'Processing...' })}</span>
            </div>
          ) : disabled ? (
            <span>{t('ecommerce.createYourPoster')}</span>
          ) : (
            <>
              <div className="flex flex-col items-start gap-0.5">
                <span>{t('ecommerce.goToCheckout', { defaultValue: 'Přejít k pokladně' })}</span>
                <span className="flex items-baseline gap-2 text-sm font-normal">
                  <span className="line-through opacity-70">{basePrice.toFixed(2)}€</span>
                  <span className="text-lg">{discountedPrice.toFixed(2)}€</span>
                </span>
              </div>
              <ChevronRight className="w-5 h-5 flex-shrink-0" />
            </>
          )}
        </Button>
      </div>
    </div>
  );
}
